import React, { useState } from 'react';
import { Target, TrendingUp, Users, Calendar, BarChart3, PieChart, Mail, MessageSquare } from 'lucide-react';
import { colors } from '../../../constants/theme';
import CampaignPlanner from './CampaignPlanner';
import CampaignBuilder from './CampaignBuilder';
import TargetingOptimizer from './TargetingOptimizer';
import PerformanceAnalytics from './PerformanceAnalytics';

const MarketingAgent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'campaign' | 'builder' | 'targeting' | 'analytics'>('campaign');

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-3">
        {/* コンパクトヘッダー */}
        <div className="mb-2">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold" style={{ color: colors.text.primary }}>
              企画支援エージェント
            </h2>
            <p className="text-sm" style={{ color: colors.text.secondary }}>
              顧客分析と最適キャンペーン
            </p>
          </div>
        </div>

        {/* コンパクトKPI */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <MetricCard
            title="アクティブキャンペーン"
            value="12"
            change="+3 今月"
            icon={<Target />}
            color={colors.accent.red}
          />
          <MetricCard
            title="リーチ顧客数"
            value="45.2K"
            change="+18%"
            icon={<Users />}
            color={colors.accent.blue}
          />
          <MetricCard
            title="平均CTR"
            value="3.8%"
            change="+0.5%"
            icon={<TrendingUp />}
            color={colors.ui.success}
          />
          <MetricCard
            title="ROI"
            value="425%"
            change="+52%"
            icon={<BarChart3 />}
            color={colors.accent.purple}
          />
        </div>

        {/* タブナビゲーション */}
        <div className="flex space-x-1 mb-3 border-b" style={{ borderColor: colors.ui.border }}>
          <TabButton
            active={activeTab === 'campaign'}
            onClick={() => setActiveTab('campaign')}
            label="キャンペーン計画"
            icon={<Calendar />}
          />
          <TabButton
            active={activeTab === 'builder'}
            onClick={() => setActiveTab('builder')}
            label="キャンペーンビルダー"
            icon={<Target />}
          />
          <TabButton
            active={activeTab === 'targeting'}
            onClick={() => setActiveTab('targeting')}
            label="ターゲティング"
            icon={<Users />}
          />
          <TabButton
            active={activeTab === 'analytics'}
            onClick={() => setActiveTab('analytics')}
            label="分析・レポート"
            icon={<PieChart />}
          />
        </div>

        {/* メインコンテンツ */}
        <div className="space-y-6">
          {activeTab === 'campaign' && <CampaignPlanner />}
          {activeTab === 'builder' && <CampaignBuilder />}
          {activeTab === 'targeting' && <TargetingOptimizer />}
          {activeTab === 'analytics' && <PerformanceAnalytics />}
        </div>
      </div>
    </div>
  );
};

// メトリックカード
interface MetricCardProps {
  title: string;
  value: string;
  change: string;
  icon: React.ReactNode;
  color: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, change, icon, color }) => {
  return (
    <div 
      className="p-2 rounded-lg border bg-white"
      style={{ borderColor: colors.ui.border }}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs" style={{ color: colors.text.secondary }}>
          {title}
        </span>
        {React.isValidElement(icon) ? React.cloneElement(icon, { size: 14, style: { color } }) : icon}
      </div>
      <div className="text-lg font-bold" style={{ color: colors.text.primary }}>
        {value}
      </div>
      <div className="text-xs" style={{ color: colors.text.secondary }}>
        {change}
      </div>
    </div>
  );
};

// タブボタン
interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, label, icon }) => {
  return (
    <button
      onClick={onClick}
      className={`px-2 py-1 text-xs font-medium border-b-2 transition-colors flex items-center space-x-1 ${
        active 
          ? 'border-red-500' 
          : 'border-transparent hover:border-gray-300'
      }`}
      style={{ 
        color: active ? colors.accent.red : colors.text.secondary,
        borderBottomColor: active ? colors.accent.red : 'transparent'
      }}
    >
      {React.isValidElement(icon) ? React.cloneElement(icon, { size: 12 }) : icon}
      <span>{label}</span>
    </button>
  );
};

export default MarketingAgent;